import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { inr, imgUrl, categoryLabel, roomGstRate, gstPct, isWholeUnit } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Star, BadgePercent, Wifi, Coffee, Tv, Car, Users, Bed, ArrowLeft, Home, DoorOpen } from "lucide-react";
import DirectContact from "@/components/hotel/DirectContact";
import { HostProfile } from "@/components/hotel/HostProfile";
import { PhotoGallery } from "@/components/PhotoGallery";

const FALLBACK = "https://images.pexels.com/photos/237371/pexels-photo-237371.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const AMENITY_ICONS = {
  wifi: Wifi, breakfast: Coffee, tv: Tv, parking: Car,
};

export default function HotelDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/hotels/${id}`);
        setData(res.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="py-20 text-center text-[#717171]">Loading...</div>;
  if (!data) return <div className="py-20 text-center text-[#717171]">Hotel not found.</div>;
  const { hotel, rooms, host } = data;
  const images = (hotel.images && hotel.images.length > 0) ? hotel.images.map(imgUrl) : [FALLBACK];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#717171] mb-4 hover:text-[#222]">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold mb-2">{hotel.name}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-[#717171]">
            <span data-testid="hotel-category-pill" className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#F0F0F4] text-[#222] font-medium"><Home className="w-3.5 h-3.5" /> {categoryLabel(hotel.category)}</span>
            <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-current text-[#FFB400]" /> {hotel.star_rating || 4}-star</span>
            {hotel.total_room_count > 0 && (
              <span data-testid="hotel-total-rooms" className="flex items-center gap-1"><DoorOpen className="w-4 h-4" /> {hotel.total_room_count} rooms</span>
            )}
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {hotel.address}, {hotel.city}</span>
          </div>
        </div>
        <Badge data-testid="zero-commission-badge" className="bg-[#FF385C]/10 text-[#FF385C] hover:bg-[#FF385C]/15 border-0">
          <BadgePercent className="w-3.5 h-3.5 mr-1" /> 0% commission · Better price
        </Badge>
      </div>

      {/* gallery */}
      <PhotoGallery images={images} title={hotel.name} testId="hotel-gallery" />

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <section>
            <h2 className="text-xl font-display font-semibold mb-2">About this stay</h2>
            <p className="text-[#444] leading-relaxed whitespace-pre-line">{hotel.description}</p>
          </section>

          {hotel.amenities && hotel.amenities.length > 0 && (
            <section>
              <h2 className="text-xl font-display font-semibold mb-3">What this place offers</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {hotel.amenities.map((a) => {
                  const Icon = AMENITY_ICONS[a.toLowerCase()] || Star;
                  return (
                    <div key={a} className="flex items-center gap-2 text-sm text-[#222] py-2">
                      <Icon className="w-4 h-4 text-[#717171]" /> {a}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section>
            <h2 className="text-xl font-display font-semibold mb-3">Choose your room</h2>
            {rooms.length === 0 ? (
              <p className="text-[#717171] py-6">No rooms available yet. Please check back soon.</p>
            ) : (
              <div className="space-y-4">
                {rooms.map((r) => (
                  <div key={r.id} data-testid={`room-card-${r.id}`} className="bg-white border border-[#EBEBEB] rounded-2xl p-5 flex flex-col sm:flex-row gap-4">
                    <img src={r.images?.[0] ? imgUrl(r.images[0]) : FALLBACK} alt={r.name} className="w-full sm:w-48 aspect-[4/3] rounded-xl object-cover" onError={(e) => { e.currentTarget.src = FALLBACK; }} />
                    <div className="flex-1">
                      <h3 className="font-display font-semibold text-lg mb-1">{r.name}</h3>
                      <p className="text-sm text-[#717171] mb-2">{r.description}</p>
                      <div className="flex flex-wrap gap-3 text-xs text-[#444]">
                        <span className="flex items-center gap-1"><Bed className="w-3.5 h-3.5" /> {r.room_type || "Double"} · {r.bed_size || r.bed_type || "Double"} bed</span>
                        <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Up to {r.max_guests || r.capacity || 2} guests{isWholeUnit(hotel.category) ? " (whole place)" : ""}</span>
                        {!isWholeUnit(hotel.category) && (r.number_of_rooms > 1) && <span>· {r.number_of_rooms} rooms available</span>}
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end justify-between">
                      <div>
                        <div className="font-display font-bold text-2xl">{inr(r.price_per_night)}</div>
                        <div className="text-xs text-[#717171]">per night{isWholeUnit(hotel.category) ? " · whole place" : " · per room"}</div>
                        <div className="text-[11px] text-[#717171]">+ {gstPct(roomGstRate(r.price_per_night))} GST & fees</div>
                      </div>
                      <Button
                        data-testid={`book-room-btn-${r.id}`}
                        onClick={() => navigate(`/book/${hotel.id}/${r.id}`)}
                        className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white"
                      >
                        Reserve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <HostProfile host={host} />
        </div>

        <aside className="lg:sticky lg:top-28 self-start h-fit">
          <div className="bg-white border border-[#EBEBEB] rounded-2xl p-6 shadow-md">
            <p className="text-sm text-[#717171]">Starts from</p>
            <p className="font-display font-bold text-3xl mb-2" data-testid="sidebar-price">
              {hotel.min_price ? inr(hotel.min_price) : (rooms[0] ? inr(rooms[0].price_per_night) : "—")}
              <span className="text-base font-normal text-[#717171]"> /night</span>
            </p>
            <div className="bg-[#FFF2F4] rounded-xl p-3 text-xs text-[#222] mb-4">
              <BadgePercent className="w-4 h-4 inline mr-1 text-[#FF385C]" />
              You save up to <strong>{inr((hotel.min_price || rooms[0]?.price_per_night || 0) * 0.30)}</strong> per night (~30%) vs commission-based OTAs.
            </div>
            {rooms[0] ? (
              <Button
                data-testid="sidebar-reserve-btn"
                className="w-full rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white h-12 text-base font-semibold"
                onClick={() => navigate(`/book/${hotel.id}/${rooms[0].id}`)}
              >
                Reserve now
              </Button>
            ) : (
              <Button disabled className="w-full rounded-xl h-12">No rooms available yet</Button>
            )}

            <div className="relative my-4 text-center">
              <span className="bg-white px-3 text-xs text-[#717171] relative z-10">or contact host directly</span>
              <div className="absolute inset-x-0 top-1/2 h-px bg-[#EBEBEB]" />
            </div>

            <DirectContact hotel={hotel} rooms={rooms} variant="sidebar" />

            <p className="text-[11px] text-[#717171] text-center mt-3">Free cancellation up to 24h before check-in</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
